import { useMemo, useState } from "react";
import { useBranch } from "@/context/BranchContext";
import { useSettings } from "@/context/SettingsContext";
import {
  defaultBranchSettings,
  type BranchSettings,
  type DayKey,
  type PickupMethodKey,
} from "@/mocks/branch/settings";
import { mockBranchInfo } from "@/mocks/branch/employee";
import AccessDenied from "@/pages/branch/access-denied/page";
import ConfirmDialog from "@/pages/branch/orders/components/ConfirmDialog";
import WorkingHoursSection from "./components/WorkingHoursSection";
import EmergencyClosureSection from "./components/EmergencyClosureSection";
import PickupPolicySection from "./components/PickupPolicySection";
import ContactInfoSection from "./components/ContactInfoSection";
import SettingsSaveBar from "./components/SettingsSaveBar";
import { WEEK_DAYS, isValidContactPhone } from "./constants";

interface ToastState {
  type: "success" | "error";
  message: string;
}

export default function SettingsPage() {
  const { can, employee } = useBranch();
  const { settings, saveAll } = useSettings();

  const [draft, setDraft] = useState<BranchSettings>(settings);
  const [phoneError, setPhoneError] = useState("");
  const [invalidDays, setInvalidDays] = useState<DayKey[]>([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = (type: ToastState["type"], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 2800);
  };

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(settings),
    [draft, settings],
  );

  if (!can("workingHours") && !can("pickupPolicy") && !can("emergencyClosure")) {
    return <AccessDenied />;
  }

  const canHours = can("workingHours");
  const canPickup = can("pickupPolicy");
  const canEmergency = can("emergencyClosure");

  const emergencyActive = draft.emergencyClosure.active;

  const updateDayEnabled = (day: DayKey, enabled: boolean) => {
    setInvalidDays((prev) => prev.filter((d) => d !== day));
    setDraft((prev) => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: { ...prev.workingHours[day], enabled },
      },
    }));
  };

  const updateDayTime = (day: DayKey, field: "open" | "close", value: string) => {
    setInvalidDays((prev) => prev.filter((d) => d !== day));
    setDraft((prev) => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: { ...prev.workingHours[day], [field]: value },
      },
    }));
  };

  const updateEmergency = (active: boolean) => {
    setDraft((prev) => ({
      ...prev,
      emergencyClosure: { ...prev.emergencyClosure, active },
    }));
    showToast("success", active ? "تم إغلاق الفرع مؤقتًا" : "تمت إعادة فتح الفرع");
  };

  const updateEmergencyReason = (reason: string) => {
    setDraft((prev) => ({
      ...prev,
      emergencyClosure: { ...prev.emergencyClosure, reason },
    }));
  };

  const updateMethod = (key: PickupMethodKey, value: boolean) => {
    setDraft((prev) => ({
      ...prev,
      pickupPolicy: {
        ...prev.pickupPolicy,
        methods: { ...prev.pickupPolicy.methods, [key]: value },
      },
    }));
  };

  const updateEstimated = (minutes: number) => {
    setDraft((prev) => ({
      ...prev,
      pickupPolicy: { ...prev.pickupPolicy, estimatedMinutes: minutes },
    }));
  };

  const updatePayOnPickup = (value: boolean) => {
    setDraft((prev) => ({
      ...prev,
      pickupPolicy: { ...prev.pickupPolicy, payOnPickup: value },
    }));
  };

  const updatePhone = (phone: string) => {
    setPhoneError("");
    setDraft((prev) => ({ ...prev, contactPhone: phone }));
  };

  const handleSave = () => {
    let hasError = false;
    const badDays: DayKey[] = [];

    if (!isValidContactPhone(draft.contactPhone)) {
      setPhoneError("أدخل رقم تواصل صحيح (مثال: 0112345678)");
      hasError = true;
    } else {
      setPhoneError("");
    }

    WEEK_DAYS.forEach((day) => {
      const s = draft.workingHours[day.key];
      if (s.enabled && s.open === s.close) {
        badDays.push(day.key);
      }
    });
    setInvalidDays(badDays);
    if (badDays.length) hasError = true;

    if (hasError) {
      showToast("error", "تحقّق من الحقول المميّزة قبل الحفظ");
      return;
    }

    saveAll(draft);
    showToast("success", "تم حفظ تغييرات إعدادات الفرع");
  };

  const handleDiscard = () => {
    setDraft(settings);
    setPhoneError("");
    setInvalidDays([]);
    showToast("success", "تم تجاهل التغييرات غير المحفوظة");
  };

  const handleReset = () => {
    setResetOpen(false);
    saveAll(defaultBranchSettings);
    setDraft(defaultBranchSettings);
    setPhoneError("");
    setInvalidDays([]);
    showToast("success", "تمت استعادة الإعدادات الافتراضية");
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4 pb-2">
      {/* بطاقة معلومات الفرع والحالة */}
      <div className="bg-background-50 rounded-card border border-background-200 px-4 md:px-5 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-foreground-900">
                {employee?.branchName}
              </h2>
              <span className="text-sm text-foreground-400">{employee?.storeName}</span>
            </div>
            <p className="text-sm text-foreground-500 mt-1.5 flex items-center gap-2 flex-wrap">
              <i className="ri-map-pin-2-line text-base text-foreground-400"></i>
              {mockBranchInfo.address}
            </p>
          </div>

          <div
            className={`flex items-center gap-2.5 px-4 py-2.5 rounded-full border shrink-0 ${
              emergencyActive
                ? "border-red-200 bg-red-50"
                : "border-primary-200 bg-primary-50"
            }`}
          >
            <span className="relative flex h-2.5 w-2.5">
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${
                  emergencyActive ? "bg-red-500" : "bg-primary-500"
                }`}
              ></span>
              <span
                className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                  emergencyActive ? "bg-red-500" : "bg-primary-500"
                }`}
              ></span>
            </span>
            <span
              className={`text-sm font-bold whitespace-nowrap ${
                emergencyActive ? "text-red-700" : "text-primary-700"
              }`}
            >
              {emergencyActive ? "مغلق مؤقتًا" : "مفتوح ويستقبل الطلبات"}
            </span>
          </div>
        </div>
      </div>

      {/* بيانات التواصل */}
      <ContactInfoSection
        phone={draft.contactPhone}
        address={mockBranchInfo.address}
        onChangePhone={updatePhone}
        error={phoneError}
      />

      {/* أوقات العمل */}
      {canHours ? (
        <WorkingHoursSection
          workingHours={draft.workingHours}
          onToggleDay={updateDayEnabled}
          onChangeTime={updateDayTime}
          invalidDays={invalidDays}
        />
      ) : null}

      {/* الإغلاق الطارئ */}
      {canEmergency ? (
        <EmergencyClosureSection
          active={emergencyActive}
          reason={draft.emergencyClosure.reason}
          onToggle={updateEmergency}
          onChangeReason={updateEmergencyReason}
        />
      ) : null}

      {/* سياسة الاستلام */}
      {canPickup ? (
        <PickupPolicySection
          methods={draft.pickupPolicy.methods}
          estimatedMinutes={draft.pickupPolicy.estimatedMinutes}
          payOnPickup={draft.pickupPolicy.payOnPickup}
          deliveryEnabled={settings.delivery.enabled}
          deliveryFee={settings.delivery.fee}
          onToggleMethod={updateMethod}
          onChangeEstimated={updateEstimated}
          onTogglePayOnPickup={updatePayOnPickup}
        />
      ) : null}

      {/* استعادة الافتراضي */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setResetOpen(true)}
          className="h-11 px-4 rounded-btn text-sm font-bold text-foreground-500 border border-background-300 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="inline-flex items-center gap-2">
            <i className="ri-refresh-line text-lg"></i>
            استعادة الإعدادات الافتراضية
          </span>
        </button>
      </div>

      {/* شريط الحفظ */}
      <SettingsSaveBar dirty={dirty} onSave={handleSave} onDiscard={handleDiscard} />

      {resetOpen ? (
        <ConfirmDialog
          title="استعادة الإعدادات الافتراضية"
          message="سيتم إرجاع جميع إعدادات الفرع (أوقات العمل، سياسة الاستلام، رقم التواصل) إلى القيم الافتراضية. هل تريد المتابعة؟"
          confirmLabel="استعادة الافتراضي"
          cancelLabel="رجوع"
          tone="danger"
          onConfirm={handleReset}
          onCancel={() => setResetOpen(false)}
        />
      ) : null}

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] flex items-center gap-2.5 px-4 py-3 rounded-btn bg-foreground-900 text-white animate-fade-slide-up">
          <div
            className={`w-5 h-5 flex items-center justify-center ${
              toast.type === "error" ? "text-red-400" : "text-primary-400"
            }`}
          >
            <i
              className={`${
                toast.type === "error" ? "ri-error-warning-line" : "ri-checkbox-circle-line"
              } text-lg`}
            ></i>
          </div>
          <span className="text-sm font-semibold whitespace-nowrap">{toast.message}</span>
        </div>
      ) : null}
    </div>
  );
}